import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { createApiClient, handleGetRequest } from "../apiService";
import { useToken } from "../tokenUtils";

import { SuccesType } from "../review/reviewTypes";
import {
  acceptAssociatedEditorTypes,
  addFileToDecisionTypes,
  addFileToTechnicalReviewTypes,
  applyTechnicalReviewTypes,
  assignAssociatedEditorTypes,
  assignReviewerTypes,
  DeleteFileFromDecisionTypes,
  editorArticleTypes,
  editorTypes,
  emitVeredictTypes,
  getReviewFileActor,
  historyTypes,
  rejectAssociatedEditorTypes,
  ServiceEditortypes,
  SuccessType,
} from "./editorTypes";

const apiClient = createApiClient("");
const domain = getSubdomain();

const handleRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};
const handlePutRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const handleDeleteRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
  const res = await apiClient.request({
    url,
    method: "DELETE",
    headers,
    data,
  });
  return res.data;
};

const createEditor = async (data: editorTypes): Promise<SuccesType> => {
  const token = useToken();

  const res = await handleRequest(`${domain}/editor/`, data, token);
  return res as SuccesType;
};

const editEditor = async ({
  data,
  slug,
}: {
  data: editorTypes;
  slug: string;
}): Promise<SuccesType> => {
  const token = useToken();

  const res = await handlePatchRequest(`${domain}/editor/${slug}`, data, token);
  return res;
};

const getArticlesByStatus = async (
  data: number[],
): Promise<editorArticleTypes[]> => {
  const token = useToken();

  const articles = await handleGetRequest(
    apiClient,
    `${domain}/editor/articles/?status=${data}`,
    token,
  );
  return articles as editorArticleTypes[];
};
const getArticlesInProduction = async (
  volumen_slug: string,
): Promise<editorArticleTypes[]> => {
  const token = useToken();

  const articles = await handleGetRequest(
    apiClient,
    `${domain}/editor/${volumen_slug}/articles/production/`,
    token,
  );
  return articles as editorArticleTypes[];
};

const getEditors = async (): Promise<editorTypes[]> => {
  const token = useToken();

  const editors = await handleGetRequest(apiClient, `${domain}/editor/`, token);
  return editors as editorTypes[];
};

const getArticlesHistory = async (slug: string): Promise<historyTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error("Authentication token not found.");
  }

  const history = await handleGetRequest(
    apiClient,
    `${domain}/editor/articles/${slug}/history`,
    token,
  );

  return history as historyTypes[];
};

const assignAssociatedEditor = async (
  data: assignAssociatedEditorTypes,
): Promise<SuccesType> => {
  if (!data.article_slug || !data.editor_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();

  const path = `${domain}/editor/articles/${data.article_slug}/assign/${data.editor_slug}?files_visible=${data.visible_files}&message=${data.message}`;

  const res = await handlePutRequest(path, {}, token);
  return res as SuccesType;
};

const applyTechnicalReview = async (
  technical_review: applyTechnicalReviewTypes,
): Promise<SuccesType> => {
  const token = useToken();
  if (!token) {
    throw new Error("Faltan token");
  }

  const path = `${domain}/editor/articles/${technical_review.article_slug}/technical_review`;
  const res = await handlePutRequest(path, technical_review.data, token);
  return res as SuccessType;
};

const assignReviewer = async (
  reviewerAssignation: assignReviewerTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const res = await handleRequest(
    `${domain}/editor/${reviewerAssignation.article_slug}/assign_reviewer/${reviewerAssignation.data.reviewer_slug}?files_visible=${reviewerAssignation.data.visible_files}`,
    { message_reviewer: reviewerAssignation.data.message_reviewer },
    token,
  );
  return res as SuccesType;
};

const sendToPublication = async (data: {
  slug: string;
  comment: string;
}): Promise<SuccesType> => {
  const token = useToken();
  if (!token) {
    throw new Error("Falta el token.");
  }
  const res = await handlePutRequest(
    `${domain}/editor/articles/${data.slug}/send_to_publication`,
    { comment: data.comment },
    token,
  );
  return res as SuccesType;
};

const sendReminderToAuthor = async (
  article_slug: string,
): Promise<SuccesType> => {
  const token = useToken();

  const res = await handlePutRequest(
    `${domain}/editor/remind/${article_slug}`,
    {},
    token,
  );
  return res as SuccesType;
};

const emitVeredict = async (
  emitVeredict: emitVeredictTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const res = await handlePutRequest(
    `${domain}/editor/articles/${emitVeredict.article_slug}/veredict`,
    emitVeredict.data,
    token,
  );
  return res as SuccesType;
};

const changesStatus = async (data: ServiceEditortypes): Promise<SuccesType> => {
  const token = useToken();

  const res = await handlePutRequest(
    `${domain}/editor/remind/${data.article_slug}`,
    data,
    token,
  );
  return res as SuccesType;
};

const getAssociatedEditors = async (): Promise<editorTypes[]> => {
  const token = useToken();

  const editors = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor`,
    token,
  );
  return editors as editorTypes[];
};

const acceptAssociatedEditor = async (
  acceptationData: acceptAssociatedEditorTypes,
): Promise<SuccesType> => {
  if (!acceptationData.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }

  const token = useToken();
  if (!token) {
    throw new Error("Faltan token se debe iniciar session ");
  }
  const path = `${domain}/associated_editor/articles/${acceptationData.article_slug}/accept`;
  const res = await handlePutRequest(path, acceptationData.data, token);
  return res;
};

const rejectAssociatedEditor = async (
  rejectionData: rejectAssociatedEditorTypes,
): Promise<SuccesType> => {
  if (!rejectionData.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();

  const path = `${domain}/associated_editor/articles/${rejectionData.article_slug}/reject`;
  const res = await handlePutRequest(path, rejectionData, token);
  return res;
};

const getEditorArticlesByVolume = async (
  volume_slug: string,
): Promise<SuccesType> => {
  const token = useToken();

  return await handleGetRequest(
    apiClient,
    `${domain}/volumes/${volume_slug}/articles`,
    token,
  );
};

const handlePutFileRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(url, data, { headers });
  return res.data;
};
const handlePostFileRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const addFileToTechnicalReview = async (
  data: addFileToTechnicalReviewTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const path = `${domain}/editor/articles/${data.article_slug}/technical_review_file`;
  const res = await handlePutFileRequest(path, data.file, token);
  return res as SuccesType;
};

const deleteEditor = async (slug: string): Promise<SuccesType> => {
  const token = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  const res = await apiClient.delete(`${domain}/editor/${slug}`, { headers });
  return res.data as SuccesType;
};

const addFileToDecisionAssocEditor = async (
  data: addFileToDecisionTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const path = `${domain}/articles/${data.article_slug}/assoc_editor/${data.actor_slug}/add_file`;
  const res = await handlePostFileRequest(path, data.file, token);
  return res;
};

const addFileToDecisionEditor = async (
  data: addFileToDecisionTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const path = `${domain}/articles/${data.article_slug}/editor/${data.actor_slug}/add_file`;
  const res = await handlePostFileRequest(path, data.file, token);
  return res;
};

const getReviewFileReviewer = async (
  payload: getReviewFileActor,
): Promise<string> => {
  const token = useToken();

  const path = `${domain}/articles/${payload.article_slug}/reviewer/${payload.actor_slug}/file`;
  const res = await handleGetRequest(apiClient, path, token);
  return res as string;
};

const getReviewFileAssocEditor = async (
  payload: getReviewFileActor,
): Promise<string> => {
  const token = useToken();

  const path = `${domain}/articles/${payload.article_slug}/assoc_editor/${payload.actor_slug}/file`;
  const res = await handleGetRequest(apiClient, path, token);
  return res as string;
};

const deleteFileFromDecision = async (
  data: DeleteFileFromDecisionTypes,
): Promise<SuccesType> => {
  const token = useToken();

  const path = `${domain}/editor/articles/${data.article_slug}/delete_file?filename=${encodeURIComponent(data.filename)}`;
  const res = await handleDeleteRequest(path, {}, token);
  return res as SuccesType;
};

const getEditorReviewBySlug = async (slug: string): Promise<any> => {
  const token = useToken();

  if (!slug) {
    throw new Error("Slug parameter is required");
  }
  const response = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor/${slug}`,
    token,
  );
  return response;
};

const getAssociatedEditorArticlesByStatus = async (
  data: number[],
): Promise<editorArticleTypes[]> => {
  const token = useToken();

  const articles = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor/articles/?status=${data}`,
    token,
  );
  return articles as editorArticleTypes[];
};

const getAssociatedEditorReviewBySlug = async (
  slug: string,
): Promise<editorTypes[]> => {
  const token = useToken();

  if (!slug) {
    throw new Error("Slug parameter is required");
  }
  const editors = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor/slug/${slug}`,
    token,
  );
  return editors as editorTypes[];
};

const getPendingReviewsAssocEd = async (): Promise<editorArticleTypes[]> => {
  const token = useToken();

  const articles = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor/pending/reviews/`,
    token,
  );
  return articles as editorArticleTypes[];
};

const updateReviewVisibility = async (data: {
  article_slug: string;
  review_id: number;
  visible_by_author: boolean;
}): Promise<SuccessType> => {
  const token = useToken();
  const res = await apiClient.patch(
    `${domain}/editor/articles/${data.article_slug}/reviews/${data.review_id}/visibility`,
    { visible_by_author: data.visible_by_author },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data as SuccessType;
};

const getCompletedArticlesAssocEditor = async (): Promise<
  editorArticleTypes[]
> => {
  const token = useToken();

  const articles = await handleGetRequest(
    apiClient,
    `${domain}/associated_editor/articles/completed/`,
    token,
  );
  return articles as editorArticleTypes[];
};

const reloadOpenSearch = async (
  articleSlug: string,
): Promise<{
  status: string;
  publication: string;
}> => {
  const token = useToken();

  const res = await handleRequest(
    `/views/${domain}/reload/article/${articleSlug}`,
    {},
    token,
  );
  return res as { status: string; publication: string };
};

const reloadVolumeOpenSearch = async (
  volumeSlug: string,
): Promise<{
  status: string;
  publication: string;
}> => {
  const token = useToken();

  const res = await handleRequest(
    `/views/${domain}/reload/volume/${volumeSlug}`,
    {},
    token,
  );
  return res as { status: string; publication: string };
};

const editorService = {
  assignAssociatedEditor,
  applyTechnicalReview,
  getArticlesHistory,
  getEditors,
  createEditor,
  editEditor,
  getArticlesByStatus,
  assignReviewer,
  sendToPublication,
  sendReminderToAuthor,
  emitVeredict,
  changesStatus,
  getAssociatedEditors,
  acceptAssociatedEditor,
  rejectAssociatedEditor,
  getEditorArticlesByVolume,
  addFileToTechnicalReview,
  deleteEditor,
  addFileToDecisionAssocEditor,
  addFileToDecisionEditor,
  getReviewFileReviewer,
  getReviewFileAssocEditor,
  deleteFileFromDecision,
  getEditorReviewBySlug,
  getAssociatedEditorArticlesByStatus,
  getAssociatedEditorReviewBySlug,
  getPendingReviewsAssocEd,
  updateReviewVisibility,
  getCompletedArticlesAssocEditor,
  getArticlesInProduction,
  reloadOpenSearch,
  reloadVolumeOpenSearch,
};

export default editorService;
